import type { GetAssessmentProgressResponse } from '@libs/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@api';

import { queryKeys } from '../../../constants/query-keys';
import { isRunActive } from '../utils/run-progress';

/**
 * How often the run screen asks for progress.
 *
 * A run takes 30-45 minutes and emits a few hundred events over that time, so
 * the interesting rate is "a document finishes every minute or two", not
 * per-second. Five seconds keeps the screen feeling live without turning a
 * 40-minute visit into 2,400 requests.
 */
const POLL_MS = 5_000;

async function fetchAssessmentProgress(
  assessmentId: string,
  page: number,
  limit: number,
  signal?: AbortSignal,
): Promise<GetAssessmentProgressResponse> {
  const response = await apiClient.get<GetAssessmentProgressResponse>(
    `api/assessments/${assessmentId}/progress`,
    { params: { page, limit }, signal },
  );

  return response.data;
}

export const useAssessmentProgress = (
  assessmentId: string | undefined,
  { page, limit }: { page: number; limit: number },
) =>
  useQuery({
    queryKey: queryKeys.assessments.progress(assessmentId ?? '', page, limit),
    queryFn: ({ signal }) => fetchAssessmentProgress(assessmentId as string, page, limit, signal),
    enabled: Boolean(assessmentId),
    // Each page is its own cache entry, so without this a page flip would empty
    // the list and re-run the skeleton before the next page landed — on a screen
    // that is also polling, which would make it flicker on its own.
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      // Stopped on error rather than left ticking, the same rule the remediation
      // watcher settled on: a failed fetch leaves the last good `data` in place,
      // so a status check alone would go on seeing an active run and hammer a
      // failing endpoint forever. The mount refetch and window-focus refetch
      // both still fire, so it heals itself.
      if (query.state.status === 'error') return false;
      const status = query.state.data?.status;
      if (!status) return POLL_MS;
      // Shared with the page's elapsed ticker and with `deriveRunView`, so
      // "this run can still change" cannot come to mean two things.
      return isRunActive(status) ? POLL_MS : false;
    },
    // A poll that fails should not blank the screen a user is watching.
    retry: 1,
  });
